import type { EventApiResponse } from "@/lib/humhub-api/types";

export interface ReminderFormData {
  unit: number;
  value: number;
}

export type EventFormData = {
  title: string;
  language: string;
  startDate: string;
  endDate: string;
  channel: string;
  location: string;
  is_draft: number;
  online_location: string;
  location_type: number;
  reminders: ReminderFormData[];
  description: string;
  files: File[];
  is_public: number;
  hidden: number;
  isAllDay: boolean;
  timeZone?: string;
  startTime?: string;
  endTime?: string;
  recurrence?: import("@/lib/humhub-api/types").RecurrenceRule | null;
  scope?: import("@/lib/humhub-api/types").RecurrenceScope;
};

export interface CalendarViewState {
  currentDate: Date;
  viewStartDate: string;
  viewEndDate: string;
  selectedDayEvents: {
    date: Date;
    events: EventApiResponse[];
  } | null;
  selectedEvent: EventApiResponse | null;
}

export interface EventOperationResult {
  success: boolean;
  message?: string;
  eventId?: number;
}

// 型ガード関数を削除（EventDetail, EventListItem用）
// isEventDetail, isEventListItem を削除

// EventFormData用の型ガードのみ保持
export const isEventFormData = (data: unknown): data is EventFormData => {
  return (
    data !== null &&
    typeof data === "object" &&
    "title" in data &&
    typeof (data as Partial<EventFormData>).title === "string" &&
    "startDate" in data &&
    typeof (data as Partial<EventFormData>).startDate === "string" &&
    "endDate" in data &&
    typeof (data as Partial<EventFormData>).endDate === "string"
  );
};

// モーダルの種類
export enum ModalType {
  EVENT_DETAIL = "event_detail",
  EVENT_CREATE = "event_create",
  EVENT_EDIT = "event_edit",
  EVENT_DELETE = "event_delete",
  EVENT_PUBLISH = "event_publish",
  EVENT_DUPLICATE = "EVENT_DUPLICATE",
  DAY_EVENTS = "day_events",
}

// モーダル設定の型定義
export interface ModalConfig {
  maxWidth: string;
  maxHeight?: string;
}

// モーダル設定
export const MODAL_CONFIGS: Record<ModalType, ModalConfig> = {
  [ModalType.EVENT_DETAIL]: { maxWidth: "max-w-4xl" },
  [ModalType.EVENT_CREATE]: {
    maxWidth: "max-w-lg",
    maxHeight: "max-h-[90vh]",
  },
  [ModalType.EVENT_EDIT]: { maxWidth: "max-w-2xl" },
  [ModalType.EVENT_DUPLICATE]: {
    maxWidth: "max-w-4xl",
    maxHeight: "max-h-[95vh]",
  },
  [ModalType.EVENT_DELETE]: { maxWidth: "max-w-md" },
  [ModalType.EVENT_PUBLISH]: { maxWidth: "max-w-lg" },
  [ModalType.DAY_EVENTS]: { maxWidth: "max-w-4xl", maxHeight: "80vh" },
};

// モーダル状態の型定義
export interface ModalState {
  activeModal: ModalType | null;
  selectedEvent: EventApiResponse | null;
  selectedEventApiData: EventApiResponse | null;
  selectedDate: Date | null;
  selectedEvents: EventApiResponse[];
  selectedDeleteScope?: import("@/lib/humhub-api/types").RecurrenceScope | null;
  previousModalState: {
    activeModal: ModalType | null;
    selectedEvent: EventApiResponse | null;
    selectedEventApiData: EventApiResponse | null;
    selectedDate: Date | null;
    selectedEvents: EventApiResponse[];
    selectedDeleteScope?:
      | import("@/lib/humhub-api/types").RecurrenceScope
      | null;
  } | null;
}

// モーダルアクション
export interface ModalActions {
  openEventDetail: (event: EventApiResponse) => void;
  openEventCreate: (date?: Date) => void;
  openEventEdit: (
    event: EventApiResponse,
    eventApiData: EventApiResponse,
  ) => void;
  openEventDuplicate: (
    event: EventApiResponse,
    eventApiData: EventApiResponse,
  ) => void;
  openEventDelete: (
    event: EventApiResponse,
    eventApiData: EventApiResponse,
    scope?: import("@/lib/humhub-api/types").RecurrenceScope,
  ) => void;
  openEventPublish: (
    event: EventApiResponse,
    eventApiData: EventApiResponse,
  ) => void;
  openDayEvents: (date: Date, events: EventApiResponse[]) => void;
  closeModal: (options?: { reset?: boolean }) => void;
}

// モーダル管理のためのプロパティ
export interface ModalManagerProps {
  state: ModalState;
  actions: ExtendedModalActions;
  onDataRefresh: () => void;
}

export interface ExtendedModalActions extends ModalActions {}
